/**
 * Pendências financeiras — leitura e baixa. As regras de "o que é
 * pendência" vivem em `utils/receivables.ts`; aqui só Firestore.
 *
 * Sem coleção própria: a pendência é o agendamento com cobrança em aberto.
 * A consulta é só `chargedAt >= início do controle` (índice de campo único,
 * sem composto) e o resto do filtro — status, dia, cancelado, saldo — é em
 * memória.
 */
import {
  collection,
  deleteField,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from './config';
import { normalizeAppointment } from './appointments';
import type { Appointment, AppointmentPayment, PaymentPart } from '@/types/models';
import { isOpenReceivable, RECEIVABLES_START_MS } from '@/utils/receivables';
import { compositionLabel, PAYMENT_METHOD_SHORT } from '@/utils/paymentMethods';
import { remainingBalance } from '@/utils/appointmentCharge';

type Method = PaymentPart['method'];

export interface ReceivableTerms {
  /** null limpa o campo. */
  paymentDueDate: number | null;
  paymentAgreedMethod: Method | null;
  /** Só pro convênio. null limpa (volta a "valor a definir"). */
  insurerAmount?: number | null;
  receivableNote: string;
}

export interface SettleInput {
  settledAt: number;
  amount: number;
  method: Method;
  actorUid: string;
  actorName: string;
  note?: string;
}

const apptRef = (clinicId: string, id: string) => doc(db, 'clinics', clinicId, 'appointments', id);

export const ReceivableService = {
  /** Toda cobrança com dívida em aberto da clínica (qualquer dia). */
  async listOpen(clinicId: string): Promise<Appointment[]> {
    const snap = await getDocs(
      query(
        collection(db, 'clinics', clinicId, 'appointments'),
        where('chargedAt', '>=', RECEIVABLES_START_MS),
      ),
    );
    return snap.docs.map((d) => normalizeAppointment(d.data())).filter((a) => isOpenReceivable(a));
  },

  /** Baixas feitas a partir de `sinceMs` (histórico "Recebidos"). */
  async listSettled(clinicId: string, sinceMs: number): Promise<Appointment[]> {
    const snap = await getDocs(
      query(
        collection(db, 'clinics', clinicId, 'appointments'),
        where('receivableSettledAt', '>=', sinceMs),
      ),
    );
    return snap.docs
      .map((d) => normalizeAppointment(d.data()))
      .filter((a) => !a.isDeleted)
      .sort((a, b) => (b.receivableSettledAt ?? 0) - (a.receivableSettledAt ?? 0));
  },

  /** Pendências (abertas) + baixas de um paciente — pra ficha. */
  async listForPatient(clinicId: string, patientId: string): Promise<Appointment[]> {
    const snap = await getDocs(
      query(collection(db, 'clinics', clinicId, 'appointments'), where('patientId', '==', patientId)),
    );
    return snap.docs
      .map((d) => normalizeAppointment(d.data()))
      .filter((a) => isOpenReceivable(a) || Boolean(a.receivableSettledAt));
  },

  /** Grava o combinado: data prevista, forma, valor do convênio, observação. */
  async updateTerms(clinicId: string, id: string, t: ReceivableTerms): Promise<void> {
    await updateDoc(apptRef(clinicId, id), {
      paymentDueDate: t.paymentDueDate ?? deleteField(),
      paymentAgreedMethod: t.paymentAgreedMethod ?? deleteField(),
      ...(t.insurerAmount !== undefined
        ? { insurerAmount: t.insurerAmount ?? deleteField() }
        : {}),
      receivableNote: t.receivableNote.trim() || deleteField(),
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Baixa: "recebi". Cobrança "A pagar"/sinal (de paciente OU convênio) →
   * vira PAGO (com a forma e a entrada no histórico de pagamentos), e a
   * pendência some sozinha em todo lugar que lê `paymentStatus`. "Convênio
   * paga" → só carimba a baixa; o status CONVENIO fica (é o que diz que o
   * paciente não pagou do bolso).
   */
  async settle(clinicId: string, a: Appointment, s: SettleInput): Promise<void> {
    const stamp = {
      receivableSettledAt: s.settledAt,
      receivableSettledBy: s.actorUid,
      receivableSettledByName: s.actorName,
      receivableSettledAmount: s.amount,
      receivableSettledMethod: s.method,
      updatedAt: serverTimestamp(),
    };
    if (a.paymentStatus === 'CONVENIO') {
      await updateDoc(apptRef(clinicId, a.id), stamp);
      return;
    }
    if (a.paymentStatus !== 'PENDENTE' && a.paymentStatus !== 'PARCIAL') {
      throw new Error('Esta cobrança não tem valor a receber.');
    }

    const total = a.chargedAmount ?? 0;
    const alreadyPaid = total - remainingBalance(a);
    // PARCIAL já tem as partes do sinal; soma a quitação como mais uma.
    // PENDENTE não tinha forma nenhuma (ou só a combinada) — a quitação é tudo.
    const prevParts = a.paymentStatus === 'PARCIAL' ? a.paymentParts ?? [] : [];
    const parts: PaymentPart[] = [...prevParts, { method: s.method, amount: s.amount }];
    const entry: AppointmentPayment = {
      amount: s.amount,
      at: s.settledAt,
      method: PAYMENT_METHOD_SHORT[s.method],
      by: s.actorUid,
      byName: s.actorName,
      note: s.note?.trim() || 'quitação',
    };
    await updateDoc(apptRef(clinicId, a.id), {
      ...stamp,
      paymentStatus: 'PAGO',
      amountPaid: alreadyPaid + s.amount,
      // Recebeu diferente do saldo (desconto na quitação): o total cobrado
      // passa a ser o que de fato entrou, pra os relatórios fecharem.
      ...(Math.abs(s.amount - remainingBalance(a)) > 0.005
        ? { chargedAmount: alreadyPaid + s.amount }
        : {}),
      paymentParts: parts,
      paymentMethod: compositionLabel(parts),
      payments: [...(a.payments ?? []), entry],
    });
  },

  /** Desfaz a baixa de um "Convênio paga" (lançada por engano). A baixa
   *  que virou PAGO se desfaz na cobrança da agenda (voltando pra "A pagar"). */
  async reopenInsurer(clinicId: string, id: string): Promise<void> {
    await updateDoc(apptRef(clinicId, id), {
      receivableSettledAt: deleteField(),
      receivableSettledBy: deleteField(),
      receivableSettledByName: deleteField(),
      receivableSettledAmount: deleteField(),
      receivableSettledMethod: deleteField(),
      updatedAt: serverTimestamp(),
    });
  },
};
